import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  useDeleteSkillFrameworkMutation,
  useDuplicateSkillFrameworkMutation,
  useGetSkillFrameworkQuery,
  usePublishSkillFrameworkMutation,
} from '@/state/skills/skills-api';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Layers, Plus, Sparkles } from 'lucide-react';
import type { SkillFrameworkListItem } from '@/state/skills/skills-api';
import { normalizeSkillDescription } from './components/skill-framework-chart-utils';
import CreateSkillFrameworkForm from './components/create-skill-framework-form';
import EditSkillFrameworkForm from './components/edit-skill-framework-form';
import SkillFrameworkActions from './components/skill-framework-hierarchy-actions';
import { toast } from 'sonner';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { getMessageByStatus } from '@/lib/error-message';
import { useClientPermissions } from '@/hooks/use-client-permissions';

const SkillsAndCompetences = () => {
  const { tenant } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFramework, setEditFramework] = useState<{
    id: string | number;
    name: string;
    description: string;
    skillTypeId: number | null;
    parentId: null;
  } | null>(null);
  const {
    data: frameworks,
    isLoading,
    error,
    refetch,
  } = useGetSkillFrameworkQuery({ tenant: tenant || '' });
  const { isSchoolAdmin, isTeacher, hasAIModule } = useClientPermissions();
  const canManage = isSchoolAdmin || isTeacher;
  const canGenerate = canManage && hasAIModule;
  const [publishSkillFramework, { isLoading: isPublishing }] =
    usePublishSkillFrameworkMutation();
  const [duplicateSkillFramework, { isLoading: isDuplicating }] =
    useDuplicateSkillFrameworkMutation();
  const [deleteSkillFramework, { isLoading: isDeleting }] =
    useDeleteSkillFrameworkMutation();

  const handleError = (err: unknown, fallback: string) => {
    const apiError = err as ApiErrorResponse;
    toast.error(fallback, {
      description: getMessageByStatus(apiError?.status || 500),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading frameworks. Please try again later.
      </div>
    );
  }

  return (
    <>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Skills & Competences
              </h1>
            </div>
            {canManage && (
              <div className="flex flex-wrap items-center gap-2">
                {canGenerate && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-background hover:bg-accent/60"
                    onClick={() =>
                      navigate(`/${tenant}/admin-tools/skills-and-competences/generate`)
                    }
                  >
                    <Sparkles className="h-4 w-4" />
                    Generate
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Create
                </button>
              </div>
            )}
          </div>
        </div>
        {!frameworks || frameworks.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No skill frameworks found.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {frameworks.map((framework: SkillFrameworkListItem) => {
              const description = normalizeSkillDescription(
                framework.description
              );
              return (
                <div
                  key={framework.id}
                  className="group cursor-pointer h-full"
                  onClick={() => navigate(`${framework.id}/`)}
                >
                  <Card className="flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200 group-hover:bg-accent/5 relative overflow-hidden border-l-0">
                    {/* Hover line */}
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-muted group-hover:bg-primary transition-colors duration-200 z-10" />

                    <CardHeader className="bg-muted/20 pb-4 pl-6">
                      <div className="flex justify-between items-start gap-2">
                        <div className="space-y-1">
                          <CardTitle className="text-lg leading-tight flex items-center gap-2">
                            {framework.name}
                          </CardTitle>
                          {description && (
                            <CardDescription className="line-clamp-2 text-xs">
                              {description}
                            </CardDescription>
                          )}
                        </div>
                        <div
                          className="flex items-center gap-2 shrink-0"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {!framework.published && (
                            <Badge
                              variant="secondary"
                              className="shrink-0 bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
                            >
                              Draft
                            </Badge>
                          )}
                          {canManage && (
                            <SkillFrameworkActions
                              isRoot
                              isPublished={framework.published}
                              isPublishing={isPublishing}
                              onPublish={() => {
                                if (!tenant || isPublishing) return;
                                void (async () => {
                                  try {
                                    await publishSkillFramework({
                                      tenant,
                                      id: framework.id,
                                      published: true,
                                    }).unwrap();
                                    toast.success('Framework published');
                                    refetch();
                                  } catch (err) {
                                    handleError(err, 'Publish failed');
                                  }
                                })();
                              }}
                              onEdit={(nodeId) => {
                                setEditFramework({
                                  id: nodeId,
                                  name: framework.name,
                                  description: framework.description,
                                  skillTypeId: framework.skill_type?.id ?? null,
                                  parentId: null,
                                });
                                setIsEditModalOpen(true);
                              }}
                              isDuplicating={isDuplicating}
                              onDuplicate={(nodeId) => {
                                if (!tenant || isDuplicating || isDeleting)
                                  return;
                                void (async () => {
                                  try {
                                    await duplicateSkillFramework({
                                      tenant,
                                      id: nodeId,
                                      rootId: framework.id,
                                    }).unwrap();
                                    toast.success('Framework duplicated');
                                    refetch();
                                  } catch (err) {
                                    handleError(err, 'Duplicate failed');
                                  }
                                })();
                              }}
                              isDeleting={isDeleting}
                              onDelete={(nodeId) => {
                                if (!tenant || isDeleting) return;
                                void (async () => {
                                  try {
                                    await deleteSkillFramework({
                                      tenant,
                                      id: nodeId,
                                      rootId: framework.id,
                                    }).unwrap();
                                    toast.success('Framework deleted');
                                    refetch();
                                  } catch (err) {
                                    handleError(err, 'Delete failed');
                                  }
                                })();
                              }}
                              nodeId={String(framework.id)}
                            />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardFooter className="mt-auto pl-6">
                      <div className="flex items-center justify-between w-full text-xs text-muted-foreground pt-4 border-t border-muted">
                        <div className="flex items-center gap-1">
                          <Layers className="h-3 w-3" />
                          {framework.children?.length || 0}
                        </div>
                        <Badge
                          variant="outline"
                          className="text-xs font-normal bg-background"
                        >
                          {framework.skill_type?.name}
                        </Badge>
                      </div>
                    </CardFooter>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <CreateSkillFrameworkForm
        tenant={tenant || ''}
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={() => refetch()}
      />
      {editFramework && (
        <EditSkillFrameworkForm
          tenant={tenant || ''}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditFramework(null);
          }}
          nodeId={editFramework.id}
          rootId={editFramework.id}
          nodeName={editFramework.name}
          nodeDescription={editFramework.description}
          nodeSkillTypeId={editFramework.skillTypeId}
          currentParentId={editFramework.parentId}
          parentOptions={[]}
          isRoot
          onUpdated={() => refetch()}
        />
      )}
    </>
  );
};

export default SkillsAndCompetences;
